import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiEndPoints, ApiRoot } from '../../shared/constants/api-endpoints';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class VenueTokenService {
  private readonly storageKey = 'venue_service_token';
  private inFlight$?: Observable<string>;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  ensureVenueToken(): Observable<string> {
    const cached = this.getValidCachedToken();
    if (cached) {
      return of(cached);
    }

    if (!this.inFlight$) {
      this.inFlight$ = this.requestNewToken().pipe(
        shareReplay(1),
        finalize(() => {
          this.inFlight$ = undefined;
        })
      );
    }

    return this.inFlight$;
  }

  private getValidCachedToken(): string | null {
    const token = String(localStorage.getItem(this.storageKey) ?? '').trim();
    if (!token) {
      return null;
    }

    const payload = this.decodeJwtPayload(token);
    const exp = Number(payload?.['exp'] ?? 0);
    if (!exp) {
      return null;
    }

    const bufferMs = 60_000;
    return exp * 1000 - bufferMs > Date.now() ? token : null;
  }

  private requestNewToken(): Observable<string> {
    const subjectToken = String(this.authService.getAccessToken() ?? '').trim();
    if (!subjectToken) {
      return throwError(() => new Error('Login access token is required to exchange the venue token'));
    }

    return this.requestTokenExchange(subjectToken).pipe(
      switchMap((exchangeToken) => this.requestLiferayToken(exchangeToken)),
      map((token) => {
        this.persistToken(token);
        return token;
      }),
      catchError((error) => {
        console.error('Failed to fetch venue token', error);
        return throwError(() => error);
      })
    );
  }

  private requestTokenExchange(subjectToken: string): Observable<string> {
    const clientId = String(environment.token_exchange_clientId ?? '').trim();
    if (!clientId) {
      return throwError(() => new Error('Token exchange client ID is not configured'));
    }

    const body = new HttpParams()
      .set('client_id', clientId)
      .set('grant_type', 'urn:ietf:params:oauth:grant-type:token-exchange')
      .set('subject_token_type', 'urn:ietf:params:oauth:token-type:jwt')
      .set('subject_token', subjectToken)
      .set('requested_token_type', 'urn:ietf:params:oauth:token-type:access_token')
      .set('scope', 'openid address email profile');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const tokenExchangeUrl = `${gatewayOrigin}/authentication/oauth2/token`;

    return this.http.post<any>(tokenExchangeUrl, body.toString(), { headers }).pipe(
      map((response) => {
        const token = String(response?.access_token ?? '').trim();
        if (!token) {
          throw new Error('Token exchange response missing access_token');
        }
        return token;
      })
    );
  }

  private requestLiferayToken(exchangeToken: string): Observable<string> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${exchangeToken}`,
    });

    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const venueTokenUrl = `${gatewayOrigin}/v2/${ApiRoot.LIFERAY_TOKEN_REST}/${ApiEndPoints.LIFERAY_TOKEN}`;

    return this.http.post<any>(venueTokenUrl, '', { headers }).pipe(
      map((res: any) => {
        const token = String(res?.access_token ?? '').trim();
        if (!token) {
          throw new Error('Venue token response missing access_token');
        }
        return token;
      })
    );
  }

  private persistToken(token: string): void {
    if (!token) {
      return;
    }
    localStorage.setItem(this.storageKey, token);
  }

  private decodeJwtPayload(token: string): Record<string, any> | null {
    const parts = String(token ?? '').split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const normalized = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
      return JSON.parse(window.atob(padded));
    } catch {
      return null;
    }
  }
}
